import type React from 'react';
import { cn } from '@/lib/utils';
import { useValidationSchema } from '@/context';
import { InfoIcon } from '@/components/InfoIcon';
import { isFieldRequired } from '@/utility/utility';
import { useFormikContext, type FieldMetaProps } from 'formik';
import type { ReactNode } from 'react';
import styles from './FormField/FormField.module.css';
import { Info } from 'lucide-react';

/**
 * Props for the Label component
 */
interface LabelProps {
  name: string;
  label?: string;
  labelHint?: string;
  infoText?: string | React.ReactNode;
  disabled?: boolean;
  labelButton?: ReactNode;
}

/**
 * Props for the Error component
 */
interface ErrorProps<T> {
  errorMeta: FieldMetaProps<T>;
}

/**
 * Props for the FieldWrapper component
 */
export interface FieldWrapperConfig extends LabelProps {
  ref?: React.Ref<HTMLDivElement>;
  fieldClassName?: string;
  showErrorSpace?: boolean;
}

/**
 * Props for the Wrapper component
 */
interface FieldWrapperProps<T> extends FieldWrapperConfig, ErrorProps<T> {
  children: React.ReactNode;
}

/**
 * A wrapper component for form fields that renders a label, the field
 * itself and an optional error message.
 *
 * @param  props - The props
 * object should contain the name of the field, the label text, the field
 * component itself, whether to show the error message, the class name
 * for the field and any additional props for the label.
 *
 * @returns A JSX element representing the field wrapper.
 */
export function FieldWrapper<T>({
  ref,
  children,
  errorMeta,
  fieldClassName,
  showErrorSpace = true,
  ...labelProps
}: FieldWrapperProps<T>) {
  return (
    <div
      ref={ref}
      className={cn(styles.fieldWrapper, 'relative', fieldClassName)}
    >
      <Label {...labelProps} errorMeta={errorMeta} />
      {children}
      {showErrorSpace && <Error errorMeta={errorMeta} />}
    </div>
  );
}

/**
 * A component that renders a label with an optional info icon.
 * The label will be disabled if the `disabled` prop is set to `true`.
 * The info icon will only be displayed if the `infoText` prop is provided.
 */
function Label({
  label,
  labelHint,
  name,
  infoText,
  disabled,
  labelButton,
  errorMeta,
}: LabelProps & { errorMeta?: FieldMetaProps<unknown> }) {
  const schema = useValidationSchema();
  const { values } = useFormikContext();

  if (!label) return null;

  const isRequired = isFieldRequired(
    schema,
    name,
    values as Record<string, unknown>
  );

  const hasError = errorMeta?.touched && !!errorMeta?.error;

  return (
    <label
      htmlFor={name}
      className={cn(
        styles.fieldLabel,
        'flex items-center justify-between gap-1',
        hasError && styles.fieldLabelError,
        disabled && styles.fieldLabelDisabled
      )}
    >
      <span className="flex items-center gap-1 truncate">
        {label}
        {labelHint && <span className="text-[8px]">{labelHint}</span>}
        <InfoIcon display={!!infoText} size={14} content={infoText} />
        {isRequired && <span className="text-red-600"> *</span>}
      </span>
      {labelButton && <span className="shrink-0">{labelButton}</span>}
    </label>
  );
}

/**
 * A component that renders an error message if the field has been touched
 * and contains an error.
 *
 * The error message will be rendered with an opacity of 0 and a negative z-index
 * until the field has been touched. When the field has been touched, the error
 * message will fade in with an animation.
 *
 * @param props - The props object should contain the error
 * message and a boolean indicating whether the field has been touched.
 *
 * @returns A JSX element representing the error message.
 */
function Error<T>({ errorMeta }: ErrorProps<T>) {
  if (!errorMeta) return null;

  const { error, touched } = errorMeta;
  const showError = touched && !!error;

  return (
    <div className="h-5 relative">
      <p
        className={cn(
          styles.fieldErrorText,
          'leading-none transition-all duration-300 opacity-0 ease-in-out absolute flex items-center gap-1',
          showError && 'opacity-100 top-0'
        )}
      >
        <Info size={10} />
        {error}
      </p>
    </div>
  );
}
